import { find } from "lodash"

type Point = [number, number]
type Path = Point[]

export const parseSVGPath1 = (d: string): Path[] => {
  const commands = d.match(/[a-df-z][^a-df-z]*/gi)
  if (!commands) return []

  let currentPoint: Point = [0, 0]
  let currentPath: Path = []
  let paths: Path[] = []

  for (const command of commands) {
    const type = command[0]
    const values = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
    let i = 0

    switch (type) {
      case "Q": {
        while (i + 3 < values.length) {
          const cp: Point = [values[i++], values[i++]]
          const end: Point = [values[i++], values[i++]]
          const start = [...currentPoint]

          const numSegments = 20
          for (let t = 1 / numSegments; t <= 1; t += 1 / numSegments) {
            const x =
              (1 - t) * (1 - t) * start[0] +
              2 * (1 - t) * t * cp[0] +
              t * t * end[0]
            const y =
              (1 - t) * (1 - t) * start[1] +
              2 * (1 - t) * t * cp[1] +
              t * t * end[1]
            currentPath.push([x, y])
          }

          currentPoint = end
        }
        break
      }

      case "q": {
        while (i + 3 < values.length) {
          const cp: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          const end: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          const start = [...currentPoint]

          const numSegments = 20
          for (let t = 1 / numSegments; t <= 1; t += 1 / numSegments) {
            const x =
              (1 - t) * (1 - t) * start[0] +
              2 * (1 - t) * t * cp[0] +
              t * t * end[0]
            const y =
              (1 - t) * (1 - t) * start[1] +
              2 * (1 - t) * t * cp[1] +
              t * t * end[1]
            currentPath.push([x, y])
          }

          currentPoint = end
        }
        break
      }

      case "M": // Move to (absolute)
        if (currentPath.length) paths.push(currentPath)
        currentPath = []
        currentPoint = [values[i++], values[i++]]
        currentPath.push([...currentPoint])
        break

      case "m": // Move to (relative)
        if (currentPath.length) paths.push(currentPath)
        currentPath = []
        currentPoint = [
          currentPoint[0] + values[i++],
          currentPoint[1] + values[i++],
        ]
        currentPath.push([...currentPoint])
        break

      case "L": // Line to (absolute)
        while (i < values.length) {
          currentPoint = [values[i++], values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "l": // Line to (relative)
        while (i < values.length) {
          currentPoint = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPath.push([...currentPoint])
        }
        break

      case "H": // Horizontal line (absolute)
        while (i < values.length) {
          currentPoint = [values[i++], currentPoint[1]]
          currentPath.push([...currentPoint])
        }
        break

      case "h": // Horizontal line (relative)
        while (i < values.length) {
          currentPoint = [currentPoint[0] + values[i++], currentPoint[1]]
          currentPath.push([...currentPoint])
        }
        break

      case "V": // Vertical line (absolute)
        while (i < values.length) {
          currentPoint = [currentPoint[0], values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "v": // Vertical line (relative)
        while (i < values.length) {
          currentPoint = [currentPoint[0], currentPoint[1] + values[i++]]
          currentPath.push([...currentPoint])
        }
        break

      case "C": // Cubic Bézier Curve (absolute)
        while (i + 5 < values.length) {
          const cp1: Point = [values[i++], values[i++]]
          const cp2: Point = [values[i++], values[i++]]
          currentPoint = [values[i++], values[i++]]
          currentPath.push([...currentPoint]) // Store only endpoint
        }
        break

      case "c": // Cubic Bézier Curve (relative)
        while (i + 5 < values.length) {
          const cp1: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          const cp2: Point = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPoint = [
            currentPoint[0] + values[i++],
            currentPoint[1] + values[i++],
          ]
          currentPath.push([...currentPoint]) // Store only endpoint
        }
        break

      case "Z":
      case "z": // Close path
        if (currentPath.length) {
          currentPath.push([...currentPath[0]]) // Close the path
          paths.push(currentPath)
          currentPath = []
        }
        break

      default:
        console.warn(`Unsupported command: ${type}`)
    }
  }

  if (currentPath.length) {
    paths.push(currentPath)
  }

  return paths
}
function parsePathToPoints(path: string): Point[] {
  const pathCommands = path.match(/[a-zA-Z][^a-zA-Z]*/g)
  if (!pathCommands) return []

  var points: Point[] = []
  pathCommands.forEach((command) => {
    const coords = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
    for (let i = 0; i < coords.length; i += 2) {
      if (!isNaN(coords[i]) && !isNaN(coords[i + 1])) {
        points.push([coords[i], coords[i + 1]])
      }
    }
  })

  return points
}

// Example usage:
const svgPath =
  "m 268.93432,534.29664 c -2.65506,-1.385 -3.28957,-3.405 -2.80522,-8.93 0.57962,-6.614 0.55111,-30.594 -0.0996,-83.729 -0.30535,-24.933 -0.49281,-58.422 -0.41659,-74.424 l 0.13857,-29.091 -6.81752,-0.451 c -10.321,-0.681 -51.47099,-0.541 -79.64096,0.276 -5.06824,0.138 -9.32278,0.257 -13.01517,0.333 -3.69239,0.07 -6.82263,0.138 -9.64216,0.138 -2.81955,0 -5.32842,0 -7.77811,-0.07 -2.4497,-0.07 -4.84024,-0.138 -7.42309,-0.274 -7.5094,-0.358 -14.43224,-0.812 -19.70305,-1.267 -5.27079,-0.453 -8.88951,-0.913 -9.79065,-1.279 -1.71958,-0.706 -3.31723,-5.217 -3.63355,-10.265 -0.33271,-5.309 0.0198,-5.712 4.43193,-5.061 12.27416,1.815 40.33016,2.285 104.55744,1.758 39.54244,-0.324 47.2765,-0.502 47.84539,-1.102 0.56069,-0.594 0.68169,-4.364 0.69654,-21.714 0.01,-11.548 0.26296,-30.621 0.56241,-42.39 0.29982,-11.778 0.40907,-29.63 0.24322,-39.726 -0.62178,-37.852 -0.83438,-74.498 -0.4536,-78.185 l 0.39413,-3.819 2.41136,-1.385 c 4.69695,-2.694 8.6403,-2.305 10.46085,1.031 1.90828,3.496 1.9071,3.427 1.63017,93.611 -0.14699,47.877 -0.39555,88.452 -0.55227,90.171 l -0.28497,3.121 17.76342,0.279 c 9.76989,0.161 21.99539,0.51 27.16776,0.793 27.9494,1.535 69.36093,2.142 105.13582,1.549 14.03172,-0.233 15.82334,0.05 17.38206,2.751 0.51743,0.895 0.94158,2.415 0.94256,3.379 0.002,2.303 -1.39293,8.263 -2.12736,9.082 -1.33521,1.498 -23.98041,1.231 -89.82547,-0.637 -14.32355,-0.405 -57.4841,-0.745 -64.18169,-0.754 h -12.17744 l 0.0117,1.656 c 0.005,0.909 0.2122,20.794 0.45829,44.185 0.24607,23.39 0.49452,65.859 0.55211,94.37 l 0.10474,51.843 -1.53099,1.668 c -2.754,3.002 -7.87889,4.207 -10.9901,2.586 z"
const result = parseSVGPath1(svgPath)

const generateThinPlusSign = (
  center: Point,
  length: number,
  thickness: number,
): Path => {
  const [cx, cy] = center
  const halfLength = length / 2
  const halfThickness = thickness / 2

  return [
    [cx - halfThickness, cy - halfThickness],
    [cx - halfThickness, cy - halfLength],
    [cx + halfThickness, cy - halfLength],
    [cx + halfThickness, cy - halfThickness],
    [cx + halfLength, cy - halfThickness],
    [cx + halfLength, cy + halfThickness],
    [cx + halfThickness, cy + halfThickness],
    [cx + halfThickness, cy + halfLength],
    [cx - halfThickness, cy + halfLength],
    [cx - halfThickness, cy + halfThickness],
    [cx - halfLength, cy + halfThickness],
    [cx - halfLength, cy - halfThickness],
    [cx - halfThickness, cy - halfThickness],
  ]
}

// Example usage
const thinPlusSign = generateThinPlusSign([200, 200], 150, 0.01)
const thinPlusSign2 = generateThinPlusSign([400, 200], 150, 0.01)
type Algo = (paths: Point[][]) => Point[][]
const mst: Algo = (paths) => {
  return paths
}

type Connection = [Point, Point]

const distance = (p1: Point, p2: Point): number =>
  Math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2)

const findMST = (points: Point[]): Connection[] => {
  const edges: { u: Point; v: Point; weight: number }[] = []
  const parent = new Map(points.map((p) => [p, p]))

  // Generate all possible edges
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      edges.push({
        u: points[i],
        v: points[j],
        weight: distance(points[i], points[j]),
      })
    }
  }

  // Sort edges by weight
  edges.sort((a, b) => a.weight - b.weight)

  // Find function for union-find
  const find = (p: Point): Point =>
    parent.get(p) === p ? p : find(parent.get(p)!)
  const union = (p1: Point, p2: Point) => parent.set(find(p1), find(p2))

  const mst: Connection[] = []
  for (const { u, v } of edges) {
    if (find(u) !== find(v)) {
      union(u, v)
      mst.push([u, v])
    }
  }

  const filteredMST = mst.filter(([u, v]) => {
    const d = distance(u, v)
    return d < 30
  })

  return filteredMST
}

function interpolatePointPair(points: [Point, Point]): Point[] {
  const [start, end] = points
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const distance = Math.sqrt(dx * dx + dy * dy)
  const numSteps =
    Math.floor(distance / 10) == 0 ? 1 : Math.floor(distance / 10)
  const stepX = dx / numSteps
  const stepY = dy / numSteps

  return Array.from({ length: numSteps + 1 }, (_, i) => {
    if (isNaN(start[0] + stepX * i)) {
      console.log("NAN")
      console.log(start[0], stepX, i)
    }
    return [start[0] + stepX * i, start[1] + stepY * i]
  })
}

function interpolatePath(path: Path): Path {
  const interpolatedPath: Path = []

  for (let i = 0; i < path.length - 2; i++) {
    const start = path[i]
    const end = path[i + 1]
    const interpolatedPoints = interpolatePointPair([start, end])
    interpolatedPath.push(...interpolatedPoints)
  }

  return interpolatedPath
}

function interpolatePaths(paths: Path[]): Path {
  const interpolatedPaths: Path = []

  for (const path of paths) {
    const interpolatedPath = interpolatePath(path)
    interpolatedPaths.push(...interpolatedPath)
  }

  return interpolatedPaths
}

function removeClosePoints(points: Point[], threshold: number): Point[] {
  if (points.length < 2) return points
  const current = points[0]
  return [
    current,
    ...removeClosePoints(
      points.slice(1).filter((point) => {
        return (
          Math.sqrt(
            (current[0] - point[0]) ** 2 + (current[1] - point[1]) ** 2,
          ) > threshold
        )
      }),
      threshold,
    ),
  ]
}

type SvgPathsProps = {
  paths: Path[]
  stroke?: string
  strokeWidth?: number
  fill?: string
}
function SvgPaths({
  paths,
  stroke = "black",
  strokeWidth = 2,
  fill = "none",
}: SvgPathsProps) {
  //const interpolatedPath = interpolatePointPair(paths[0]);

  return (
    <svg width="500" height="600" viewBox="0 0 500 600">
      {paths.map((path, index) => {
        const d = path
          .map((point, i) => `${i === 0 ? "M" : "L"}${point[0]},${point[1]}`)
          .join(" ")
        // console.log("d", d);
        return (
          <path
            key={index}
            d={d}
            stroke={stroke}
            strokeWidth={strokeWidth}
            fill={fill}
          />
        )
      })}
    </svg>
  )
}

type SvgPointsProps = {
  points: Point[]
  color?: string
  radius?: number
}

function SvgPoints({ points, color, radius }: SvgPointsProps) {
  return (
    <svg width="500" height="600" viewBox="0 0 500 600">
      {points.map((point, index) => (
        <circle
          key={index}
          cx={point[0]}
          cy={point[1]}
          r={radius}
          fill={color}
        />
      ))}
    </svg>
  )
}

const samplePaths: Path[] = [thinPlusSign, thinPlusSign2]

const connectionsToPaths = (connections: Connection[]): Path[] => {
  let paths: Path[] = []
  let remainingConnections = [...connections]
  while (remainingConnections.length > 0) {
    const [start, end] = remainingConnections.pop()!
    const path: Path = [start, end]
    let currentPoint = end

    while (true) {
      const nextConnectionIndex = remainingConnections.findIndex(
        ([p1, p2]) =>
          (p1[0] === currentPoint[0] && p1[1] === currentPoint[1]) ||
          (p2[0] === currentPoint[0] && p2[1] === currentPoint[1]),
      )

      if (nextConnectionIndex === -1) break

      const nextConnection = remainingConnections[nextConnectionIndex]
      remainingConnections.splice(nextConnectionIndex, 1)

      if (nextConnection[0][0] !== currentPoint[0]) {
        path.push(nextConnection[0])
        currentPoint = nextConnection[0]
      } else {
        path.push(nextConnection[1])
        currentPoint = nextConnection[1]
      }
    }

    currentPoint = start

    while (true) {
      const nextConnectionIndex = remainingConnections.findIndex(
        ([p1, p2]) =>
          (p1[0] === currentPoint[0] && p1[1] === currentPoint[1]) ||
          (p2[0] === currentPoint[0] && p2[1] === currentPoint[1]),
      )

      if (nextConnectionIndex === -1) break

      const nextConnection = remainingConnections[nextConnectionIndex]
      remainingConnections.splice(nextConnectionIndex, 1)

      if (nextConnection[0][0] !== currentPoint[0]) {
        path.unshift(nextConnection[0])
        currentPoint = nextConnection[0]
      } else {
        path.unshift(nextConnection[1])
        currentPoint = nextConnection[1]
      }
    }

    paths.push(path)
  }
  return paths
}
// console.log(
//   "mst",
//   findMST(removeClosePoints(interpolatePaths(samplePaths), 20)),
// )
// console.log(
//   "connections to paths",
//   connectionsToPaths(
//     findMST(removeClosePoints(interpolatePaths(samplePaths), 20)),
//   ),
// )

export function TestSvg() {
  return (
    <div style={{ overflow: "scroll", width: 600, height: 700 }}>
      <SvgPaths
        paths={samplePaths}
        stroke="blue"
        strokeWidth={0.5}
        fill="none"
      />
      <SvgPoints points={samplePaths[0]} color="red" radius={1} />
      <SvgPoints
        points={interpolatePaths(samplePaths)}
        color="green"
        radius={1}
      />
      <SvgPoints
        radius={2}
        color="red"
        points={removeClosePoints(interpolatePaths(samplePaths), 20)}
      />
      <SvgPaths
        paths={findMST(removeClosePoints(interpolatePaths(samplePaths), 20))}
        stroke="orange"
        strokeWidth={2}
        fill="none"
      />

      <SvgPaths
        paths={connectionsToPaths(
          findMST(removeClosePoints(interpolatePaths(samplePaths), 20)),
        )}
        stroke="green"
        strokeWidth={10}
        fill="none"
      />
    </div>
  )
}
